// fishbowl.js — entry point for the app page. Wires the controls to the modules in ./app/.

import { setupLoginButton } from './login.js';
import { logEvent, auth } from './db.js';
import { fishVariants, getRandomInt, createSwimFrames } from './fishAnimation.js';
import { toISO, formatDuration, localDateStr, longDurationThresholdSec, formatHoursMinutes } from './model.js';
import { S, newId, getCurrentTask, exportData, placeholderArt } from './app/state.js';
import { saveStreakLocal } from './app/local.js';
import { saveAll } from './app/sync.js';
import { showPlaceholder, flashMessage, setDurationDisplay, setActiveTask, clearActiveTask, dropActiveTask, updateLockState, announce } from './app/taskbox.js';
import { updateStats, renderStreakCount, initStatsVisibility } from './app/statsbox.js';
import { initTagMenu } from './app/tags.js';
import { initShowAll, refreshShowAll } from './app/showall.js';
import { initOnboarding } from './app/onboarding.js';
import { bootstrapLocal, onUserLogin } from './app/session.js';

const input = document.getElementById('taskInput');
const nextBtn = document.getElementById('nextTask');
const doneBtn = document.getElementById('markDone');
const editAllBtn = document.getElementById('toggleAll');
const releaseBtn = document.getElementById('releaseTask');
const currentTaskText = document.getElementById('currentTaskText');

// ---------------------------------------------------------------------------
// Startup
// ---------------------------------------------------------------------------
showPlaceholder();
initTagMenu();
initShowAll();
initOnboarding();
initStatsVisibility(auth);
window.addEventListener('DOMContentLoaded', bootstrapLocal);
setupLoginButton('auth-btn', onUserLogin, { getExportData: exportData });

// Arriving with ?login=1 (e.g. from the stats page gate) opens the sign-in dialog.
if (new URLSearchParams(location.search).get('login') === '1') {
  window.addEventListener('DOMContentLoaded', () => setTimeout(() => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) document.getElementById('auth-btn')?.click();
    history.replaceState(null, '', location.pathname);
  }, 300));
}

// ---------------------------------------------------------------------------
// Adding tasks
// ---------------------------------------------------------------------------
function selectedTag() {
  const r = document.querySelector('input[name="tagOption"]:checked');
  return r ? r.value : '';
}
function makeTask(text) {
  return { id: newId(), text: text.replace(/->/g, '→'), tag: selectedTag(), createdOn: new Date().toISOString(), skips: [] };
}

// Enter / Ctrl+Enter belong to the task textarea only.
input.addEventListener('keydown', async e => {
  // Ctrl+Enter: add a single task and start it immediately (single-line input only)
  if (e.ctrlKey && e.key === 'Enter') {
    const value = input.value.trim();
    if (value && !value.includes('\n')) {
      e.preventDefault();
      const newTask = makeTask(value);
      S.tasks.push(newTask);
      setActiveTask(newTask);
      logEvent('add', newTask.text);
      input.value = '';
      await saveAll();
      updateStats();
      return;
    }
  }

  // Enter (without Shift): add every non-empty line as a task
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    const lines = input.value.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return;

    lines.forEach(line => {
      const cleaned = line.replace(/^[-*]\s*\[\s*[xX]?\s*\]\s*/, '').trim(); // strip "- [ ] " / "- [x] "
      const taskObj = makeTask(cleaned);
      if (!document.getElementById('directMode')?.checked) S.manualQueue.push(taskObj);
      else S.tasks.push(taskObj);
      logEvent('add', taskObj.text);
    });

    input.value = '';
    await saveAll();
    input.focus();
    updateStats();
    refreshShowAll();
  }
});

// Alt shortcuts work anywhere on the page.
document.addEventListener('keydown', e => {
  if (!e.altKey) return;
  const key = e.key.toLowerCase();
  if (key === 'n') { e.preventDefault(); nextBtn.click(); }
  else if (key === 'd') { e.preventDefault(); doneBtn.click(); }
  else if (key === 's') { e.preventDefault(); editAllBtn.click(); }
  else if (key === 'r') {
    e.preventDefault();
    const box = document.getElementById('directMode');
    if (box) { box.checked = !box.checked; box.dispatchEvent(new Event('change')); }
  }
});

// ---------------------------------------------------------------------------
// Next
// ---------------------------------------------------------------------------
nextBtn.addEventListener('click', () => {
  // Leaving a task via Next counts as a skip
  const skippedTask = getCurrentTask();
  if (skippedTask) {
    if (!Array.isArray(skippedTask.skips)) skippedTask.skips = [];
    skippedTask.skips.push(new Date().toISOString());
    logEvent('skip', skippedTask.text);
  }

  const directModeCheckbox = document.getElementById('directMode');
  const sequential = !directModeCheckbox?.checked;

  if (sequential && S.manualQueue.length > 0) {
    const nextManual = S.manualQueue.shift();
    S.tasks.push(nextManual);
    setActiveTask(nextManual);
  } else {
    if (sequential && directModeCheckbox) {
      flashMessage('No tasks in this queue.\n\nAdd tasks with Randomize OFF.', 2000);
      return;
    }
    // Random draw from the pool (narrowed to the chosen tag), never the fish just skipped
    let pool = S.currentAddTag ? S.tasks.filter(f => f.tag === S.currentAddTag) : S.tasks;
    if (pool.length > 1 && S.currentFishId !== null) pool = pool.filter(f => f.id !== S.currentFishId);
    if (pool.length === 0) { flashMessage('No tasks with selected tag', 1500); return; }
    setActiveTask(pool[Math.floor(Math.random() * pool.length)]);
  }

  saveAll(); // skip data is saved immediately
});

// ---------------------------------------------------------------------------
// Throw back
// ---------------------------------------------------------------------------
releaseBtn?.addEventListener('click', async () => {
  if (S.currentFishId === null) return;
  dropActiveTask();
  await saveAll();
});

// ---------------------------------------------------------------------------
// Done
// ---------------------------------------------------------------------------
doneBtn.addEventListener('click', async () => {
  if (S.currentFishId === null) return;
  const doneIndex = S.tasks.findIndex(t => t.id === S.currentFishId);
  if (doneIndex === -1) { dropActiveTask(); return; } // deleted elsewhere

  const now = new Date();
  const startedAt = S.currentFishStartTime;
  let durationSec = Math.max(0, Math.round((now - startedAt) / 1000));

  // A duration far outside this user's normal range is usually a timer left
  // running (overnight, over a weekend). Ask rather than silently record it.
  const threshold = longDurationThresholdSec(S.completed);
  if (durationSec > threshold) {
    const keep = window.confirm(
      `This task ran for ${formatHoursMinutes(durationSec)} — much longer than your usual tasks.\n\n` +
      `OK: keep this time\nCancel: record the time as unknown (the task still counts as done)`
    );
    if (!keep) durationSec = null;
  }

  const diff = Math.floor((durationSec || 0) / 60); // whole minutes, for display and fish size
  const formatted = durationSec === null ? '(?)' : formatDuration(durationSec);
  setDurationDisplay(durationSec === null ? 'Duration: unknown' : `Duration: ${Math.floor(diff / 60)}:${String(diff % 60).padStart(2, '0')}`);

  const fishItem = S.tasks.splice(doneIndex, 1)[0];
  S.completed.push({
    id: fishItem.id,
    text: fishItem.text,
    tag: fishItem.tag || '',
    duration: formatted,          // "(h:mm)" — kept for display and older code
    durationSec,                  // exact seconds — used for statistics
    createdOn: toISO(fishItem.createdOn),
    startedOn: startedAt.toISOString(),
    completedOn: now.toISOString()
  });
  logEvent('done', fishItem.text, formatted);

  // 🐟 A fish sized by how long the task took swims away
  const size = diff >= 30 ? 'xl' : diff >= 15 ? 'large' : diff >= 5 ? 'medium' : 'small';
  const list = fishVariants[size];
  const animation = createSwimFrames(list[getRandomInt(0, list.length - 1)]);
  const intervalMs = Math.max(30, getRandomInt(1000, 3000) / animation.length);
  let frame = 0;
  currentTaskText.classList.remove('placeholder');
  currentTaskText.setAttribute('aria-hidden', 'true');
  announce(durationSec === null ? `Done: ${fishItem.text}` : `Done: ${fishItem.text}, ${formatHoursMinutes(durationSec)}`);
  doneBtn.disabled = true;
  const swim = setInterval(() => {
    currentTaskText.textContent = animation[frame++];
    if (frame >= animation.length) {
      clearInterval(swim);
      currentTaskText.textContent = placeholderArt;
      currentTaskText.classList.add('placeholder');
      setDurationDisplay('');
      doneBtn.disabled = false;
      updateLockState();
    }
  }, intervalMs);

  clearActiveTask();
  await saveAll();
  updateStats();
  refreshShowAll();

  // 🎯 Sign-up reminder early and then every 10 catches (3, 13, 23 …) for anonymous users
  if ((!auth.currentUser || auth.currentUser.isAnonymous) && S.completed.length % 10 === 3) {
    document.getElementById('signup-reminder').classList.remove('hidden');
  }

  // 🕐 Streak, in local calendar days (same as the stats box)
  const today = localDateStr(now);
  if (S.lastCatchDate !== today) {
    const yesterday = localDateStr(new Date(now.getTime() - 864e5));
    S.streak = (S.lastCatchDate === yesterday) ? S.streak + 1 : 1;
    S.lastCatchDate = today;
    saveStreakLocal();
    await saveAll(); // streakInfo travels with buildState()
    renderStreakCount();
  }
});
